import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, LayoutDashboard, Package, FileText, Inbox, Newspaper, Briefcase, AlertTriangle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Wordmark } from "./Logo";
import AdminAnalytics from "./AdminAnalytics";
import { useAdminData } from "./useAdminData";
import AdminShipmentsTab from "./AdminShipmentsTab";
import AdminRequestsTab from "./AdminRequestsTab";
import AdminBlogTab from "./AdminBlogTab";
import AdminSystemTab from "./AdminSystemTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "shipments", label: "Shipments", icon: Package },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "applications", label: "Applications", icon: Briefcase },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "errors", label: "System", icon: AlertTriangle },
];

export default function Admin() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const { ships, quotes, leads, posts, apps, errors, reload } = useAdminData();

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Wordmark sizeClass="h-6" />
            <span className="ml-1 font-mono-x text-[10px] tracking-widest text-muted-foreground">ADMIN</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"><ExternalLink className="w-4 h-4" /> View site</Link>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-sm rounded-full border border-border px-4 py-2 hover:bg-secondary"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mb-6">Control Tower</h1>
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === tb.id ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <AdminAnalytics />}
        {tab === "shipments" && <AdminShipmentsTab ships={ships} reload={reload} />}
        {(tab === "quotes" || tab === "leads") && (
          <AdminRequestsTab kind={tab} items={tab === "quotes" ? quotes : leads} reload={reload} />
        )}
        {tab === "applications" && <AdminRequestsTab kind="applications" items={apps} reload={reload} />}
        {tab === "blog" && <AdminBlogTab posts={posts} reload={reload} />}
        {tab === "errors" && <AdminSystemTab errors={errors} reload={reload} />}
      </div>
    </div>
  );
}
